import type { AutomationRunDb } from "@repo/app-types/entities/automationRun";
import type { FC } from "react";
import { useMemo } from "react";
import { Size } from "../helpers/helpers.js";
import {
  EntityTable,
  type EntityTableColumnDefinition,
} from "../tables/entityTable.js";
import {
  AutomationRunPreviewPill,
  formatRunTime,
} from "./automationPresentation.js";

export function describeAutomationRunResult(run: AutomationRunDb): string {
  switch (run.status) {
    case "pending":
      return "Queued…";
    case "running":
      return "Running…";
    case "failed":
      return "Failed";
    case "succeeded":
      return "Completed";
    case "canceled":
      return "Canceled";
  }
}

export interface AutomationRunsTableProps {
  runs: readonly AutomationRunDb[] | undefined;
  isLoading: boolean;
  /** Called when the user activates a row, e.g. to navigate to the run. */
  onRunAction: (run: AutomationRunDb) => void;
}

export const AutomationRunsTable: FC<AutomationRunsTableProps> = (props) => {
  const { runs, isLoading, onRunAction } = props;
  const columns = useMemo<
    readonly EntityTableColumnDefinition<AutomationRunDb>[]
  >(
    () => [
      {
        id: "run-time",
        header: "Run time",
        isRowHeader: true,
        getTextValue: (run) => formatRunTime(run.started_at ?? run.created_at),
        renderCell: (run) => (
          <span className="flex items-center gap-2">
            <span className="font-medium text-foreground">
              {formatRunTime(run.started_at ?? run.created_at)}
            </span>
            {run.is_preview ? <AutomationRunPreviewPill /> : null}
          </span>
        ),
      },
      {
        id: "results",
        header: "Results",
        getTextValue: describeAutomationRunResult,
        renderCell: (run) => (
          <span className="text-muted-foreground">
            {describeAutomationRunResult(run)}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <EntityTable
      aria-label="Agent runs"
      columns={columns}
      entities={runs}
      isLoading={isLoading}
      emptyMessage="No runs yet. Use Run preview to try this agent."
      onEntityAction={onRunAction}
      size={Size.SMALL}
    />
  );
};
